import { useEffect, useMemo, useState } from 'react';
import {
  ActionIcon,
  Button,
  Grid,
  Group,
  MultiSelect,
  Paper,
  Select,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput, DatesRangeValue } from '@mantine/dates';
import { IconFileSpreadsheet, IconInfoCircle, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { MRT_PaginationState } from 'mantine-react-table';
import momentTz from 'moment-timezone';
import CatalogContainer from '../../../components/CatalogContainer/CatalogContainer';
import CatalogListHeader from '../../../components/CatalogListHeader/CatalogListHeader';
import LoaderOverlay from '../../../components/LoaderOverlay/LoaderOverlay';
import useDownloadExcel from '../../../hooks/useDownloadExcel/useDownloadExcel';
import { DEFAULT_FOLIO_PREFIX } from '../../../constants';
import { useGetOutsourcedOrdersReport } from '../../../hooks/outsourcingOrders/queries';
import {
  OutsourcedOrdersDateField,
  OutsourcedStatus
} from '../../../hooks/outsourcingOrders/interfaces';
import { useGetPartners } from '../../../hooks/partners/queries';
import { exportOutsourcingReport } from '../../../services/reports.service';
import { buildOutsourcingReportDateParams } from './dateFilters';
import { buildOutsourcingReportExportParams } from './exportParams';
import { buildOutsourcingReportRows } from './reportRows';
import TableView from './TableView';

const formatCount = (value: number) =>
  new Intl.NumberFormat('es-MX', {
    maximumFractionDigits: 2,
  }).format(value || 0);

const defaultSummary = {
  totalOutsourcedOrders: 0,
  totalQuantity: 0,
  byStatus: {
    pending: 0,
    in_process: 0,
    returned: 0,
  },
};

const statusOptions: OutsourcedStatus[] = ['returned', 'pending', 'in_process'];

function SummaryMetric({
  label,
  tooltip,
  value,
}: {
  label: string;
  tooltip: string;
  value: string;
}) {
  return (
    <Grid.Col span={{ xs: 12, sm: 6, md: 2 }}>
      <Group gap={4} wrap="nowrap">
        <Text size="sm" c="dimmed">
          {label}
        </Text>
        <Tooltip label={tooltip} multiline w={280}>
          <span aria-label={tooltip}>
            <IconInfoCircle size={14} color="currentColor" />
          </span>
        </Tooltip>
      </Group>
      <Text fw={700}>{value}</Text>
    </Grid.Col>
  );
}

const OutsourcingReport = () => {
  const { t } = useTranslation(['ReportsPage', 'FieldName', 'CommonButtons']);
  const location = useLocation();
  const { downloadExcelV2, loadingExport } = useDownloadExcel();
  const searchParams = new URLSearchParams(location.search);
  const searchParamStoreId = searchParams.get('storeId') || '';
  const timezone = useMemo(() => momentTz.tz.guess(true), []);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>('all');
  const [selectedStatuses, setSelectedStatuses] = useState<OutsourcedStatus[]>(['returned']);
  const [dateField, setDateField] = useState<OutsourcedOrdersDateField>('receivedDate');
  const [dateRange, setDateRange] = useState<DatesRangeValue>([null, null]);
  const [sequence, setSequence] = useState('');
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 25
  });

  const { data: partners, isLoading: isGetPartnersLoading } = useGetPartners(true);

  const partnerOptions = useMemo(() => {
    const options =
      partners
        ?.filter((partner) => partner._id && partner.name)
        .map((partner) => ({
          value: partner._id,
          label: partner.name,
        })) || [];

    return [
      {
        value: 'all',
        label: t('ReportsPage:outsourcingReport.filters.allPartners'),
      },
      ...options,
    ];
  }, [partners, t]);

  useEffect(() => {
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }, [selectedPartnerId, selectedStatuses, dateField, dateRange, sequence]);

  const query = useMemo(() => {
    const dateParams = buildOutsourcingReportDateParams(dateRange);
    return {
      storeId: searchParamStoreId,
      statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sequence: sequence.trim() || undefined,
      partnerId: selectedPartnerId === 'all' ? undefined : selectedPartnerId,
      dateFrom: dateParams.dateFrom,
      dateTo: dateParams.dateTo,
      dateField,
      includeSummary: true,
      sortBy: dateField,
      sortDirection: 'desc' as const,
    };
  }, [
    dateField,
    dateRange,
    pagination.pageIndex,
    pagination.pageSize,
    searchParamStoreId,
    selectedPartnerId,
    selectedStatuses,
    sequence,
  ]);

  const exportQuery = useMemo(
    () => buildOutsourcingReportExportParams(query, timezone),
    [query, timezone]
  );

  const {
    data,
    isLoading: isGetOutsourcedOrdersLoading,
    isFetching,
    isError,
  } = useGetOutsourcedOrdersReport(!!searchParamStoreId, query);

  const summary = data?.summary || defaultSummary;
  const tableInfoData = useMemo(() => buildOutsourcingReportRows(data?.data || []), [data?.data]);
  const totalRowsCount = data?.pagination?.totalDocs || 0;
  const pages = data?.pagination?.totalPages || 0;

  const clearFilters = () => {
    setSelectedPartnerId('all');
    setSelectedStatuses(['returned']);
    setDateField('receivedDate');
    setDateRange([null, null]);
    setSequence('');
  };

  return (
    <CatalogContainer pos="relative">
      <CatalogListHeader
        title={t('ReportsPage:outsourcingReport.title')}
        showCreateButton={false}
      />
      <LoaderOverlay
        visible={isGetPartnersLoading || isGetOutsourcedOrdersLoading || isFetching}
      />
      <Grid gutter="md">
        <Grid.Col span={{ xs: 12, md: 3 }}>
          <Select
            label={t('ReportsPage:outsourcingReport.filters.partner')}
            data={partnerOptions}
            value={selectedPartnerId}
            onChange={(value) => setSelectedPartnerId(value || 'all')}
            allowDeselect={false}
            disabled={isGetPartnersLoading}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 3 }}>
          <MultiSelect
            label={t('ReportsPage:outsourcingReport.filters.status')}
            data={statusOptions.map((status) => ({
              value: status,
              label: t(`FieldName:outsourcedStatuses.${status}`),
            }))}
            value={selectedStatuses}
            onChange={(values) => setSelectedStatuses(values as OutsourcedStatus[])}
            clearable
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 3 }}>
          <Select
            label={t('ReportsPage:outsourcingReport.filters.dateField')}
            data={[
              {
                value: 'receivedDate',
                label: t('ReportsPage:outsourcingReport.dateFields.receivedDate'),
              },
              {
                value: 'sentDate',
                label: t('ReportsPage:outsourcingReport.dateFields.sentDate'),
              },
            ]}
            value={dateField}
            onChange={(value) => setDateField((value || 'receivedDate') as OutsourcedOrdersDateField)}
            allowDeselect={false}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 3 }}>
          <DatePickerInput
            clearable
            type="range"
            label={t('ReportsPage:outsourcingReport.filters.dateRange')}
            placeholder={t('ReportsPage:outsourcingReport.filters.dateRange')}
            value={dateRange}
            onChange={setDateRange}
            allowSingleDateInRange
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 8 }}>
          <TextInput
            label={t('ReportsPage:outsourcingReport.filters.order')}
            value={sequence}
            leftSection={`${DEFAULT_FOLIO_PREFIX}-`}
            onChange={(event) => setSequence(event.currentTarget.value)}
          />
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 2 }} style={{ alignSelf: 'flex-end' }}>
          <Button
            fullWidth
            color="gray"
            leftSection={<IconRefresh size={16} />}
            onClick={clearFilters}
            type="button"
          >
            {t('CommonButtons:clear').toUpperCase()}
          </Button>
        </Grid.Col>
        <Grid.Col span={{ xs: 12, md: 'content' }} style={{ alignSelf: 'flex-end' }}>
          <Tooltip label={t('CommonButtons:exportList')}>
            <ActionIcon
              disabled={loadingExport || !searchParamStoreId}
              onClick={() => {
                downloadExcelV2(async () => {
                  return await exportOutsourcingReport(searchParamStoreId, exportQuery);
                }, `Reporte_Maquilacion_${searchParamStoreId}`);
              }}
              variant="filled"
              color="#1D6F42"
              aria-label="Descargar_Listado_CSV"
            >
              <IconFileSpreadsheet style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Tooltip>
        </Grid.Col>

        <Grid.Col span={12}>
          <Paper withBorder p="sm" radius="md">
            <Grid gutter="xs">
              <SummaryMetric
                label={t('ReportsPage:outsourcingReport.summary.totalOutsourcedOrders')}
                tooltip={t('ReportsPage:outsourcingReport.summaryTooltips.totalOutsourcedOrders')}
                value={formatCount(summary.totalOutsourcedOrders)}
              />
              <SummaryMetric
                label={t('ReportsPage:outsourcingReport.summary.totalQuantity')}
                tooltip={t('ReportsPage:outsourcingReport.summaryTooltips.totalQuantity')}
                value={formatCount(summary.totalQuantity)}
              />
              {summary.totalPieces !== undefined && (
                <SummaryMetric
                  label={t('ReportsPage:outsourcingReport.summary.totalPieces')}
                  tooltip={t('ReportsPage:outsourcingReport.summaryTooltips.totalPieces')}
                  value={formatCount(summary.totalPieces)}
                />
              )}
              <SummaryMetric
                label={t('ReportsPage:outsourcingReport.summary.pending')}
                tooltip={t('ReportsPage:outsourcingReport.summaryTooltips.pending')}
                value={formatCount(summary.byStatus.pending)}
              />
              <SummaryMetric
                label={t('ReportsPage:outsourcingReport.summary.inProcess')}
                tooltip={t('ReportsPage:outsourcingReport.summaryTooltips.inProcess')}
                value={formatCount(summary.byStatus.in_process)}
              />
              <SummaryMetric
                label={t('ReportsPage:outsourcingReport.summary.returned')}
                tooltip={t('ReportsPage:outsourcingReport.summaryTooltips.returned')}
                value={formatCount(summary.byStatus.returned)}
              />
            </Grid>
          </Paper>
        </Grid.Col>

        <Grid.Col span={12}>
          <TableView
            tableInfoData={tableInfoData}
            totalRowsCount={totalRowsCount}
            pages={pages}
            isError={isError}
            isLoading={isGetOutsourcedOrdersLoading || isFetching}
            pagination={pagination}
            setPagination={setPagination}
          />
        </Grid.Col>
      </Grid>
    </CatalogContainer>
  );
};

export default OutsourcingReport;
